import { createContext, PropsWithChildren, useContext, useMemo } from 'react';
import {
  UploadAttachment,
  useDeleteAttachment,
  useUploadAttachments,
} from '@issuemanager/apps/client/hooks';
import { UploadingFile } from '@diligentcorp/heracles-form-input';
import { IssueResponseDto } from '@issuemanager/api-client';

type Props = PropsWithChildren<{
  issueCode?: string;
}>;

// eslint-disable-next-line no-spaced-func
const AttachmentsContext = createContext<{
  uploadAttachments: (
    attachments: UploadingFile[],
  ) => Promise<UploadAttachment[]>;
  deleteAttachment: ReturnType<typeof useDeleteAttachment>['mutateAsync'];
  isLoading: boolean;
}>({
  uploadAttachments: async () => [],
  deleteAttachment: () => Promise.resolve({} as IssueResponseDto),
  isLoading: false,
});

function AttachmentsProvider({ issueCode = '', children }: Props) {
  const { uploadAttachments, isLoading: isUploadLoading } =
    useUploadAttachments();
  const deleteAttachment = useDeleteAttachment({ issueCode });

  const isLoading = isUploadLoading || deleteAttachment.isPending;

  const contextValue = useMemo(
    () => ({
      uploadAttachments,
      isLoading,
      deleteAttachment: deleteAttachment.mutateAsync,
    }),
    [uploadAttachments, isLoading, deleteAttachment.mutateAsync],
  );

  return (
    <AttachmentsContext.Provider value={contextValue}>
      {children}
    </AttachmentsContext.Provider>
  );
}

const useAttachmentsContext = () => useContext(AttachmentsContext);

export { AttachmentsContext, AttachmentsProvider, useAttachmentsContext };
